// Live lint for reviewer edits. Detects violations that block submission.

export type LintIssue = {
  code: string;
  label: string;
  detail?: string;
};

const TIER_NAMES = ["foundation", "active_path", "elite", "active path"];
const NUMBER_RE = /(\$?\d[\d,]*(?:\.\d+)?%?)/g;
const URL_RE = /https?:\/\/[^\s)]+/gi;
const FORM_RE =
  /\b(?:Form\s+)?(?:1099|1040|8832|W-?\d|SS-?\d|K-?\d|1120|1065|2553|8879|W-9|W-4|W-2)[A-Z0-9-]*\b/gi;

function bag(arr: string[]) {
  const m = new Map<string, number>();
  for (const x of arr) m.set(x, (m.get(x) || 0) + 1);
  return m;
}

function diffCounts(base: string[], proposed: string[]) {
  const b = bag(base);
  const p = bag(proposed);
  const added: string[] = [];
  const removed: string[] = [];
  for (const [k, v] of p) {
    const bv = b.get(k) || 0;
    for (let i = 0; i < v - bv; i++) added.push(k);
  }
  for (const [k, v] of b) {
    const pv = p.get(k) || 0;
    for (let i = 0; i < v - pv; i++) removed.push(k);
  }
  return { added, removed };
}

export function lintProposed(base: string, proposed: string): LintIssue[] {
  const issues: LintIssue[] = [];

  // Em/en dashes introduced in the proposed body (that weren't in the base)
  const emCountBase = (base.match(/[\u2013\u2014]/g) || []).length;
  const emCountProp = (proposed.match(/[\u2013\u2014]/g) || []).length;
  if (emCountProp > emCountBase) {
    issues.push({
      code: "dash",
      label: "Em or en dash introduced",
      detail: `${emCountProp - emCountBase} added. Use a comma, colon, parentheses, or two sentences instead.`,
    });
  }

  // The word "template" (member-facing forbidden word) introduced
  const tplBase = (base.match(/\btemplate/gi) || []).length;
  const tplProp = (proposed.match(/\btemplate/gi) || []).length;
  if (tplProp > tplBase) {
    issues.push({
      code: "template",
      label: "The word 'template' was added",
      detail: "Members have their own Constitution and supporting documents. Use 'sample format' or the doc's real name.",
    });
  }

  // Tier names introduced
  for (const t of TIER_NAMES) {
    const re = new RegExp(`\\b${t.replace(" ", "\\s+")}\\b`, "gi");
    const bCount = (base.match(re) || []).length;
    const pCount = (proposed.match(re) || []).length;
    if (pCount > bCount) {
      issues.push({
        code: `tier:${t}`,
        label: `Tier name introduced: "${t}"`,
      });
    }
  }

  // Numbers changed
  const nums = diffCounts(base.match(NUMBER_RE) || [], proposed.match(NUMBER_RE) || []);
  for (const a of nums.added)
    issues.push({ code: "num_add", label: "Number or amount added", detail: a });
  for (const r of nums.removed)
    issues.push({ code: "num_rem", label: "Number or amount removed", detail: r });

  // URLs changed
  const urls = diffCounts(base.match(URL_RE) || [], proposed.match(URL_RE) || []);
  for (const a of urls.added)
    issues.push({ code: "url_add", label: "URL added", detail: a });
  for (const r of urls.removed)
    issues.push({ code: "url_rem", label: "URL removed", detail: r });

  // Form numbers changed
  const forms = diffCounts(base.match(FORM_RE) || [], proposed.match(FORM_RE) || []);
  for (const a of forms.added)
    issues.push({ code: "form_add", label: "Form number added", detail: a });
  for (const r of forms.removed)
    issues.push({ code: "form_rem", label: "Form number removed", detail: r });

  return issues;
}

/**
 * Return a set of substring patterns for red-highlight in the textarea overlay.
 * Kept simple: just the tokens we flag as issues.
 */
export function highlightPatterns(): RegExp[] {
  return [
    /[\u2013\u2014]/g, // em/en dash
    /\btemplate\b/gi,
    /\b(foundation|active[_\s]path|elite)\b/gi,
  ];
}
